import { ArrowDownIcon, ArrowUpIcon } from '@heroicons/react/20/solid';
import { CursorArrowRaysIcon, UsersIcon, ServerStackIcon, BoltIcon } from '@heroicons/react/24/outline';
import { useState, useEffect } from 'react';
import Navbar from './Navbar';
import { format, addDays, addWeeks, addMonths, addYears } from 'date-fns';
import {
  VictoryChart,
  VictoryAxis,
  VictoryLine,
  VictoryBar,
  VictoryPie,
  VictoryLegend,
  VictoryTooltip,
  VictoryVoronoiContainer,
  VictoryTheme,
} from "victory";
import Checklist from './checklist';

function classNames(...classes) {
  return classes.filter(Boolean).join(' ');
}

const TABS = [
  { key: 'security', label: 'Security Events' },
  { key: 'logins', label: 'Logins' },
  { key: 'bannedIps', label: 'Banned IPs' },
  { key: 'loginAttempts', label: 'Login Attempts' },
  { key: 'checklists', label: 'Checklists' },
];

export default function Logins() {
  const [stats, setStats] = useState([
    {
      id: 1,
      name: 'Total Logins',
      stat: '-',
      icon: CursorArrowRaysIcon,
      change: '',
      changeType: 'increase',
    },
    {
      id: 2,
      name: 'Paid Users',
      stat: '-',
      icon: UsersIcon,
      change: '',
      changeType: 'increase',
    },
    {
      id: 3,
      name: 'Banned IPs',
      stat: '-',
      icon: ServerStackIcon,
      change: '',
      changeType: 'increase',
    },
    {
      id: 4,
      name: 'Security Events',
      stat: '-',
      icon: BoltIcon,
      change: '',
      changeType: 'increase',
    },
  ]);
  const [people, setPeople] = useState([]);
  const [bannedIps, setBannedIps] = useState([]);
  const [securityData, setSecurityData] = useState([]);
  const [loginAttempts, setLoginAttempts] = useState([]);
  const [selectedDate, setSelectedDate] = useState('');
  const [AIReports, setAIReports] = useState([]);
  const [activeTab, setActiveTab] = useState('security');
  const [loading, setLoading] = useState(false);
  const [editDropdown, setEditDropdown] = useState({}); // { [id]: boolean }
  const [chartStartDate, setChartStartDate] = useState('');
  const [chartEndDate, setChartEndDate] = useState('');

  const fetchStats = async () => {
    try {
      const response = await fetch('https://api.insightos.co.uk/admin/logins', {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
      });
      if (!response.ok) {
        throw new Error('Network response was not ok');
      }
      const data = await response.json();
      const logins = data.data || [];
      const amountOfUsers = data.staff || 0;
      const bannedIpsData = data.bannedIps || [];
      const securityDataData = data.security || [];
      const loginAttemptsData = data.loginAttempts || [];

      console.log('Fetched login attempts:', loginAttemptsData);

      setPeople(logins);
      setBannedIps(bannedIpsData);
      setLoginAttempts(loginAttemptsData);
      setSecurityData(securityDataData);
      setAIReports(data.aireports);

      // Filter DEFCON > 3 for stats
      const filtered = securityDataData.filter(event => event.DEFCON > 3);

      setStats([
        {
          id: 1,
          name: 'Total Logins',
          stat: logins.length,
          icon: CursorArrowRaysIcon,
          change: '',
          changeType: 'increase',
        },
        {
          id: 2,
          name: 'Paid Users',
          stat: amountOfUsers,
          icon: UsersIcon,
          change: '',
          changeType: 'increase',
        },
        {
          id: 3,
          name: 'Banned IPs',
          stat: bannedIpsData.length,
          icon: ServerStackIcon,
          change: '',
          changeType: 'increase',
        },
        {
          id: 4,
          name: 'Security Events',
          stat: filtered.length,
          icon: BoltIcon,
          change: '',
          changeType: 'increase',
        },
      ]);
    } catch (error) {
      setStats([
        {
          id: 1,
          name: 'Total Logins',
          stat: 'Error',
          icon: CursorArrowRaysIcon,
          change: '',
          changeType: 'decrease',
        },
        {
          id: 2,
          name: 'Paid Users',
          stat: 'Error',
          icon: UsersIcon,
          change: '',
          changeType: 'decrease',
        },
        {
          id: 3,
          name: 'Banned IPs',
          stat: 'Error',
          icon: ServerStackIcon,
          change: '',
          changeType: 'decrease',
        },
        {
          id: 4,
          name: 'Security Events',
          stat: 'Error',
          icon: BoltIcon,
          change: '',
          changeType: 'decrease',
        },
      ]);
    }
  };

  useEffect(() => {
    fetchStats();
    const interval = setInterval(fetchStats, 60000); // 1 minute
    return () => clearInterval(interval);
  }, []);

  const handleBootUser = async (userId) => {
    try {
      const response = await fetch('https://api.insightos.co.uk/admin/boot-user', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({ userId }),
      });
      if (!response.ok) {
        throw new Error('Network response was not ok');
      }
      await response.json();
      // Optionally, you can refresh the stats or show a success message
      alert(`User ${userId} has been booted successfully.`);
      fetchStats(); // Refresh stats after booting user

    } catch (error) {
      // handle error
    }
  };

  // Filter security data by selected date
  const filteredByDate = selectedDate
    ? securityData.filter(event =>
        format(new Date(event.created_at), 'yyyy-MM-dd') === selectedDate
      )
    : securityData;

  // match the ai reports with the security data based on date, format report.dates to match selectedDate
  const filteredAIReports = AIReports.filter(report =>
    format(new Date(report.dates), 'yyyy-MM-dd') === selectedDate
  );

  // Prepare data for VictoryChart: count events per day (filtered by chart date range)
  const eventsPerDay = securityData.reduce((acc, event) => {
    const day = format(new Date(event.created_at), 'yyyy-MM-dd');
    if (chartStartDate && day < chartStartDate) return acc;
    if (chartEndDate && day > chartEndDate) return acc;
    acc[day] = (acc[day] || 0) + 1;
    return acc;
  }, {});
  const chartData = Object.entries(eventsPerDay)
    .map(([date, count]) => ({
      x: date,
      y: count,
    }))
    .sort((a, b) => new Date(a.x) - new Date(b.x));

  // Prepare pie chart data for device_type in logins
  const deviceTypeCounts = people.reduce((acc, person) => {
    const dt = person.device_type || 'Unknown';
    acc[dt] = (acc[dt] || 0) + 1;
    return acc;
  }, {});
  const pieColors = ['#6366f1', '#10b981', '#f59e0b', '#ef4444', '#3b82f6', '#8b5cf6'];
  const pieData = Object.entries(deviceTypeCounts).map(([label, value], i) => ({
    x: label,
    y: value,
    label: `${label}\n${value}`,
  }));

  // Prepare bar chart data for selected date (24 hours)
  const eventsPerHour = Array(24).fill(0);
  if (selectedDate) {
    filteredByDate.forEach(event => {
      const hour = new Date(event.created_at).getHours();
      eventsPerHour[hour]++;
    });
  }
  const barChartData = eventsPerHour.map((count, hour) => ({
    x: `${hour.toString().padStart(2, '0')}:00`,
    y: count,
  }));

  // Row color based on DEFCON
  function getRowColor(defcon) {
    if (defcon === 3) return 'bg-yellow-300';
    if (defcon === 4) return 'bg-orange-300';
    if (defcon === 5) return 'bg-red-500 text-white';
    return '';
  }

  const handleAIReport = async (date) => {
    setLoading(true);
    if (!date) {
      alert('Please select a date to generate the AI report.');
      setLoading(false);
      return;
    }
    try {
      const response = await fetch('https://api.insightos.co.uk/admin/ai-report', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({ date }),
      });
      if (!response.ok) {
        throw new Error('Network response was not ok');
      }
      const data = await response.json();
      if (data.success) {
        setAIReports(data.report || []);
        setSelectedDate(date);
      }
    } catch (error) {
      alert('Failed to generate AI report. Please try again later.');
    }
    setLoading(false);
  };

  // Helper to parse and render AI report content with ### as new paragraph and *** as new line
  function renderAIReportContent(content) {
    return content.split(/###/).map((paragraph, idx) => (
      <p key={idx} className="mb-2">
        {paragraph.split(/\*\*\*/).map((line, i) => (
          <span key={i}>
            {line}
            {i < paragraph.split(/\*\*\*/).length - 1 && <br />}
          </span>
        ))}
      </p>
    ));
  }

  // Handle edit dropdown for banned IPs
  const handleEditDropdown = (id) => {
    setEditDropdown(prev => ({
      ...prev,
      [id]: !prev[id],
    }));
  };

  // Handle update banned_until for a banned IP
  const handleUpdateBan = async (id, duration) => {
    const ip = bannedIps.find(ip => ip.id === id);
    if (!ip) return;
    let newDate = new Date();
    switch (duration) {
      case '1d':
        newDate = addDays(newDate, 1);
        break;
      case '1w':
        newDate = addWeeks(newDate, 1);
        break;
      case '1m':
        newDate = addMonths(newDate, 1);
        break;
      case '1y':
        newDate = addYears(newDate, 1);
        break;
      default:
        return;
    }
    try {
      const response = await fetch('https://api.insightos.co.uk/admin/update-ban', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({ id, banned_until: format(newDate, 'yyyy-MM-dd HH:mm:ss') }),
      });
      if (!response.ok) throw new Error('Failed to update ban');
      // Refresh bannedIps
      fetchStats();
      setEditDropdown(prev => ({ ...prev, [id]: false }));
    } catch (e) {
      alert('Failed to update ban duration.');
    }
  };

  // Handle ban IP for a security event
  const handleBanIp = async (eventId) => {

    try {
      const response = await fetch('https://api.insightos.co.uk/admin/ban-ip', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({ eventId }),
      });
      if (!response.ok) {
        throw new Error('Network response was not ok');
      }
      const data = await response.json();
      if (data.success) {
        alert(`IP address for event ${eventId} has been banned successfully.`);
        // Refresh stats after banning IP
        fetchStats();
      } else {
        alert(`Failed to ban IP for event ${eventId}: ${data.message}`);
      }
    } catch (error) {
      console.error('Error banning IP:', error);
      alert('An error occurred while banning the IP address. Please try again later.');
    }
  };


   function renderLoginAttemptsTable() {
  const filteredLoginAttempts = loginAttempts.filter(attempt => {
    if (!selectedDate) return true;
    const attemptDate = new Date(attempt.created_at).toDateString();
    const filterDate = new Date(selectedDate).toDateString();
    return attemptDate === filterDate;
  });

  return (
    <div>
      <div className="sm:flex sm:items-center">
        <div className="sm:flex-auto">
          <h1 className="text-base font-semibold text-gray-900">Login Attempts</h1>
          <p className="mt-2 text-sm text-gray-700">
            A comprehensive log of all login attempts including successful and failed attempts.
          </p>
        </div>
        <div className="mt-4 sm:ml-16 sm:mt-0 sm:flex-none">
          <input
            type="date"
            className="block rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 text-sm"
            value={selectedDate}
            onChange={e => setSelectedDate(e.target.value)}
            placeholder="Filter by date"
          />
        </div>
      </div>
      <div className="mt-8 flow-root">
        <div className="-mx-4 -my-2 overflow-x-auto sm:-mx-6 lg:-mx-8">
          <div className="inline-block min-w-full py-2 align-middle sm:px-6 lg:px-8">
            <table className="min-w-full divide-y divide-gray-300">
              <thead>
                <tr>
                  <th scope="col" className="py-3.5 pl-4 pr-3 text-left text-xs font-semibold text-gray-900 sm:pl-3">
                    ID
                  </th>
                  <th scope="col" className="px-3 py-3.5 text-left text-xs font-semibold text-gray-900">
                    Email
                  </th>
                  <th scope="col" className="px-3 py-3.5 text-left text-xs font-semibold text-gray-900">
                    Company Code
                  </th>
                  <th scope="col" className="px-3 py-3.5 text-left text-xs font-semibold text-gray-900">
                    Status
                  </th>
                  <th scope="col" className="px-3 py-3.5 text-left text-xs font-semibold text-gray-900">
                    IP Address
                  </th>
                  <th scope="col" className="px-3 py-3.5 text-left text-xs font-semibold text-gray-900">
                    Country
                  </th>
                  <th scope="col" className="px-3 py-3.5 text-left text-xs font-semibold text-gray-900">
                    Site
                  </th>
                  <th scope="col" className="px-3 py-3.5 text-left text-xs font-semibold text-gray-900">
                    Created At
                  </th>
                  <th scope="col" className="px-3 py-3.5 text-left text-xs font-semibold text-gray-900">
                    User Agent
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {filteredLoginAttempts.map((attempt) => (
                  <tr key={attempt.id}>
                    <td className="whitespace-nowrap py-4 pl-4 pr-3 text-xs font-medium text-gray-900 sm:pl-3">
                      {attempt.id}
                    </td>
                    <td className="whitespace-nowrap px-3 py-4 text-xs text-gray-500">
                      {attempt.email}
                    </td>
                    <td className="whitespace-nowrap px-3 py-4 text-xs text-gray-500">
                      {attempt.company_code || 'N/A'}
                    </td>
                    <td className="whitespace-nowrap px-3 py-4 text-xs">
                      <span className={`inline-flex items-center rounded-full px-2 py-1 text-xs font-medium ${
                        attempt.status === 'success' 
                          ? 'bg-green-100 text-green-800' 
                          : 'bg-red-100 text-red-800'
                      }`}>
                        {attempt.status}
                      </span>
                    </td>
                    <td className="whitespace-nowrap px-3 py-4 text-xs text-gray-500">
                      {attempt.ip_address}
                    </td>
                    <td className="whitespace-nowrap px-3 py-4 text-xs text-gray-500">
                      {attempt.country || 'Unknown'}
                    </td>
                    <td className="whitespace-nowrap px-3 py-4 text-xs text-gray-500">
                      {attempt.site || 'N/A'}
                    </td>
                    <td className="whitespace-nowrap px-3 py-4 text-xs text-gray-500">
                      {new Date(attempt.created_at).toLocaleString()}
                    </td>
                    <td className="whitespace-nowrap px-3 py-4 text-xs text-gray-500 max-w-xs truncate">
                      {attempt.user_agent ? (
                        <span title={attempt.user_agent}>
                          {attempt.user_agent.length > 50 
                            ? `${attempt.user_agent.substring(0, 50)}...` 
                            : attempt.user_agent}
                        </span>
                      ) : 'N/A'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {filteredLoginAttempts.length === 0 && (
              <div className="text-center py-8">
                <p className="text-gray-500">No login attempts found for the selected criteria.</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function renderChecklistsTable() {

  return (
    <Checklist />
  );
}

  // Tab content renderers
  function renderSecurityTable() {
    return (
      <div>
      <div className="sm:flex-auto">
        <h1 className="text-base font-semibold text-gray-900">Security Events</h1>
        <p className="mt-2 text-sm text-gray-700">
        Filter and review security events by date and DEFCON level.
        </p>
      </div>
      <div className="mt-4 sm:ml-16 sm:mt-0 sm:flex-none">
        <input
        type="date"
        className="block rounded-md border-gray-300 mt-5 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 text-sm"
        value={selectedDate}
        onChange={e => setSelectedDate(e.target.value)}
        />
      </div>
      <div className="mt-4 flow-root mt-8">
        <div className="-mx-4 -my-2 overflow-x-auto sm:-mx-6 lg:-mx-8">
        <div className="inline-block min-w-full py-2 align-middle sm:px-6 lg:px-8">
          <button className='bg-indigo-500 ml-10 text-white px-4 py-2 rounded-md mb-4' onClick={() => handleAIReport(selectedDate)} disabled={loading}>
          {loading ? 'Generating...' : 'AI Review'}
          </button>
          {filteredAIReports.length > 0 && (
          <div className="bg-green-100 text-green-800 p-4 rounded-md mb-4">
            <h3 className="font-semibold">AI Report for {selectedDate}</h3>
            <ul className="list-disc pl-5">
            {filteredAIReports.map((report, index) => (
              <li key={index} className="text-sm">
              {renderAIReportContent(report.content)}
              </li>
            ))}
            </ul>
          </div>
          )}
          <table className="min-w-full divide-y divide-gray-300">
          <thead>
            <tr>
            <th className="py-3.5 pl-4 pr-3 text-left text-xs font-semibold text-gray-900 sm:pl-3">DEFCON</th>
            <th className="px-3 py-3.5 text-left text-xs font-semibold text-gray-900">Created At</th>
            <th className="px-3 py-3.5 text-left text-xs font-semibold text-gray-900">User ID</th>
            <th className="px-3 py-3.5 text-left text-xs font-semibold text-gray-900">Email</th>
            <th className="px-3 py-3.5 text-left text-xs font-semibold text-gray-900">IP Address</th>
            <th className="px-3 py-3.5 text-left text-xs font-semibold text-gray-900">Message</th>
            <th className="px-3 py-3.5 text-left text-xs font-semibold text-gray-900">Boot User</th>
            <th className="px-3 py-3.5 text-left text-xs font-semibold text-gray-900">Ban IP - 1 day</th>
            <th className="px-3 py-3.5 text-left text-xs font-semibold text-gray-900">Resolved</th>

            </tr>
          </thead>
          <tbody className="bg-white">
            {filteredByDate.length === 0 && (
            <tr>
              <td colSpan={8} className="text-center py-4 text-gray-500">No events found.</td>
            </tr>
            )}
            {filteredByDate.map((event) => (
            <tr key={event.id} className={getRowColor(event.DEFCON)}>
              <td className="whitespace-nowrap py-4 pl-4 pr-3 text-xs font-medium sm:pl-3">{event.DEFCON ?? '-'}</td>
              <td className="whitespace-nowrap px-3 py-4 text-xs">
              {event.created_at ? format(new Date(event.created_at), 'yyyy-MM-dd HH:mm') : ''}
              </td>
              <td className="whitespace-nowrap px-3 py-4 text-xs">{event.user_id ?? '-'}</td>
              <td className="whitespace-nowrap px-3 py-4 text-xs">{event.email ?? '-'}</td>
              <td className="whitespace-nowrap px-3 py-4 text-xs">{event.ip_address ?? '-'}</td>
              <td className="whitespace-nowrap px-3 py-4 text-xs">{event.message ?? '-'}</td>
              <td className="whitespace-nowrap px-3 py-4 text-xs">
                <button
                type="button"
                className="text-indigo-600 hover:text-indigo-900"
                onClick={() => handleBootUser(event.user_id)}
                >
                Boot User
                </button>
              </td>
              <td className="whitespace-nowrap px-3 py-4 text-xs">
                <button
                type="button"
                className="text-red-600 hover:text-red-900"
                onClick={() => handleBanIp(event.id)}
                >
                Ban IP - 1 day
                </button>
              </td>
            </tr>
            ))}
          </tbody>
          </table>
        </div>
        </div>
      </div>
      </div>
    );
  }

  function renderLoginsTable() {
    return (
      <div>
        <div className="sm:flex sm:items-center">
          <div className="sm:flex-auto">
            <h1 className="text-base font-semibold text-gray-900">Users</h1>
            <p className="mt-2 text-sm text-gray-700">
              A list of all the users in your account including their name, title, email and role.
            </p>
          </div>
        </div>
        {pieData.length > 0 && (
          <div className="mt-6 bg-white rounded-xl shadow-md p-4 flex flex-col items-center max-w-sm">
            <h2 className="text-base font-semibold text-gray-900 mb-2">Logins by Device Type</h2>
            <VictoryPie
              data={pieData}
              colorScale={pieColors}
              width={280}
              height={220}
              padding={{ top: 20, bottom: 20, left: 60, right: 60 }}
              labelComponent={
                <VictoryTooltip
                  flyoutStyle={{ fill: '#fff', stroke: '#6366f1', strokeWidth: 0.5 }}
                  style={{ fontSize: 10, fill: '#222' }}
                />
              }
              events={[{ target: 'data', eventHandlers: {} }]}
            />
            <div className="flex flex-wrap gap-2 justify-center mt-2">
              {Object.entries(deviceTypeCounts).map(([label, value], i) => (
                <span key={label} className="flex items-center gap-1 text-xs">
                  <span className="inline-block w-3 h-3 rounded-full" style={{ backgroundColor: pieColors[i % pieColors.length] }} />
                  {label}: {value}
                </span>
              ))}
            </div>
          </div>
        )}
        <div className="mt-4">
        <div className="mt-8 flow-root">
          <div className="-mx-4 -my-2 overflow-x-auto sm:-mx-6 lg:-mx-8">
            <div className="inline-block min-w-full py-2 align-middle sm:px-6 lg:px-8">
              <table className="min-w-full divide-y divide-gray-300">
                <thead>
                  <tr>
                    <th scope="col" className="py-3.5 pl-4 pr-3 text-left text-xs font-semibold text-gray-900 sm:pl-3">
                      Company Name
                    </th>
                    <th scope="col" className="px-3 py-3.5 text-left text-xs font-semibold text-gray-900">
                      User ID
                    </th>
                    <th scope="col" className="px-3 py-3.5 text-left text-xs font-semibold text-gray-900">
                      Log In Time
                    </th>
                     <th scope="col" className="px-3 py-3.5 text-left text-xs font-semibold text-gray-900">
                      User Agent
                    </th>
                    <th scope="col" className="px-3 py-3.5 text-left text-xs font-semibold text-gray-900">
                      Device Type
                    </th>
                    <th scope="col" className="px-3 py-3.5 text-left text-xs font-semibold text-gray-900">
                      Site
                    </th>
                    <th scope="col" className="px-3 py-3.5 text-left text-xs font-semibold text-gray-900">
                      JWT Expiries
                    </th>
                    <th scope="col" className="relative py-3.5 pl-3 pr-4 sm:pr-3">
                      <span className="sr-only">Edit</span>
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white">
                  {people.map((person) => (
                    <tr key={person.created_at} className="even:bg-gray-50">
                      <td className="whitespace-nowrap py-4 pl-4 pr-3 text-xs font-medium text-gray-900 sm:pl-3">
                        {person.CompanyName}
                      </td>
                      <td className="whitespace-nowrap px-3 py-4 text-xs text-gray-500">{person.user_id}</td>
                      <td className="whitespace-nowrap px-3 py-4 text-xs text-gray-500">
                        {person.created_at ? format(new Date(person.created_at), 'dd/MM/yyyy - HH:mm') : ''}
                      </td>
                      <td className="whitespace-nowrap px-3 py-4 text-xs text-gray-500">
                        {person.user_agent}
                      </td>
                      <td className="whitespace-nowrap px-3 py-4 text-xs text-gray-500">
                        {person.device_type}
                      </td>
                      <td className="whitespace-nowrap px-3 py-4 text-xs text-gray-500">
                        {person.site || 'N/A'}
                      </td>
                      <td className="whitespace-nowrap px-3 py-4 text-xs text-gray-500">
                        {person.expires_at ? format(new Date(person.expires_at), 'dd/MM/yyyy - HH:mm') : ''}
                      </td>
                      <td className="relative whitespace-nowrap py-4 pl-3 pr-4 text-right text-xs font-medium sm:pr-3">
                        <button
                          type="button"
                          className="text-indigo-600 hover:text-indigo-900"
                          onClick={() => handleBootUser(person.user_id)}
                        >
                          Boot User
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
        </div>
      </div>
    );
  }

  function renderBannedIpsTable() {
    return (
      <div>
        <div className="sm:flex sm:items-center mb-4">
          <div className="sm:flex-auto">
            <h1 className="text-base font-semibold text-gray-900">Banned IPs</h1>
            <p className="mt-2 text-sm text-gray-700">
              Manage banned IP addresses and their ban durations.
            </p>
          </div>
        </div>
        <div className="mt-8 flow-root">
          <div className="-mx-4 -my-2 overflow-x-auto sm:-mx-6 lg:-mx-8">
            <div className="inline-block min-w-full py-2 align-middle sm:px-6 lg:px-8">
              <table className="min-w-full divide-y divide-gray-300">
                <thead>
                  <tr>
                    <th className="py-3.5 pl-4 pr-3 text-left text-xs font-semibold text-gray-900 sm:pl-3">ID</th>
                    <th className="px-3 py-3.5 text-left text-xs font-semibold text-gray-900">IP Address</th>
                    <th className="px-3 py-3.5 text-left text-xs font-semibold text-gray-900">Banned At</th>
                    <th className="px-3 py-3.5 text-left text-xs font-semibold text-gray-900">Reason</th>
                    <th className="px-3 py-3.5 text-left text-xs font-semibold text-gray-900">Banned Until</th>
                    <th className="px-3 py-3.5 text-left text-xs font-semibold text-gray-900">Edit</th>
                  </tr>
                </thead>
                <tbody className="bg-white">
                  {bannedIps.length === 0 && (
                    <tr>
                      <td colSpan={6} className="text-center py-4 text-gray-500">No banned IPs found.</td>
                    </tr>
                  )}
                  {bannedIps.map((ip) => (
                    <tr key={ip.id} className="even:bg-gray-50">
                      <td className="whitespace-nowrap py-4 pl-4 pr-3 text-xs font-medium text-gray-900 sm:pl-3">{ip.id}</td>
                      <td className="whitespace-nowrap px-3 py-4 text-xs text-gray-500">{ip.ip_address}</td>
                      <td className="whitespace-nowrap px-3 py-4 text-xs text-gray-500">
                        {ip.banned_at ? format(new Date(ip.banned_at), 'yyyy-MM-dd HH:mm') : ''}
                      </td>
                      <td className="whitespace-nowrap px-3 py-4 text-xs text-gray-500">{ip.reason ?? '-'}</td>
                      <td className="whitespace-nowrap px-3 py-4 text-xs text-gray-500">
                        {ip.banned_until ? format(new Date(ip.banned_until), 'yyyy-MM-dd HH:mm') : '-'}
                      </td>
                      <td className="whitespace-nowrap px-3 py-4 text-xs text-gray-500 relative">
                        <button
                          className="bg-indigo-500 text-white px-2 py-1 rounded hover:bg-indigo-600"
                          onClick={() => handleEditDropdown(ip.id)}
                        >
                          Edit
                        </button>
                        {editDropdown[ip.id] && (
                          <div className="absolute z-10 mt-2 bg-white border rounded shadow-lg">
                            <button
                              className="block w-full text-left px-4 py-2 text-xs hover:bg-indigo-50"
                              onClick={() => handleUpdateBan(ip.id, '1d')}
                            >
                              1 Day
                            </button>
                            <button
                              className="block w-full text-left px-4 py-2 text-xs hover:bg-indigo-50"
                              onClick={() => handleUpdateBan(ip.id, '1w')}
                            >
                              1 Week
                            </button>
                            <button
                              className="block w-full text-left px-4 py-2 text-xs hover:bg-indigo-50"
                              onClick={() => handleUpdateBan(ip.id, '1m')}
                            >
                              1 Month
                            </button>
                            <button
                              className="block w-full text-left px-4 py-2 text-xs hover:bg-indigo-50"
                              onClick={() => handleUpdateBan(ip.id, '1y')}
                            >
                              1 Year
                            </button>
                          </div>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <Navbar />
      <svg className="absolute h-0 w-0" aria-hidden="true" focusable="false">
        <defs>
          <linearGradient id="chartGradient" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#6366f1" stopOpacity={0.25} />
            <stop offset="100%" stopColor="#6366f1" stopOpacity={0.02} />
          </linearGradient>
          <linearGradient id="barChartGradient" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#6366f1" stopOpacity={0.5} />
            <stop offset="100%" stopColor="#6366f1" stopOpacity={0.08} />
          </linearGradient>
        </defs>
      </svg>
      {/* User stats */}
      <div>
        <div>
          <dl className="mt-5 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
            {stats.map((item) => (
              <div
                key={item.id}
                className="relative overflow-hidden rounded-xl bg-gradient-to-br from-indigo-600 via-indigo-500 to-indigo-700 px-4 pb-8 pt-4 shadow-md sm:px-5 sm:pt-5"
              >
                <div className="flex items-center">
                  <div className="absolute rounded-md bg-white/20 p-2 backdrop-blur">
                    <item.icon aria-hidden="true" className="size-5 text-white" />
                  </div>
                  <p className="ml-12 truncate text-xs font-medium text-white/80">{item.name}</p>
                </div>
                <div className="ml-12 flex items-baseline pb-4 sm:pb-5">
                  <p className="text-xl font-bold text-white drop-shadow">{item.stat}</p>
                  {item.change !== '' && (
                    <p
                      className={classNames(
                        item.changeType === 'increase' ? 'text-green-200' : 'text-red-200',
                        'ml-2 flex items-baseline text-xs font-semibold'
                      )}
                    >
                      {item.changeType === 'increase' ? (
                        <ArrowUpIcon aria-hidden="true" className="size-4 shrink-0 self-center text-green-200" />
                      ) : (
                        <ArrowDownIcon aria-hidden="true" className="size-4 shrink-0 self-center text-red-200" />
                      )}
                      <span className="sr-only">
                        {item.changeType === 'increase' ? 'Increased' : 'Decreased'} by
                      </span>
                      {item.change}
                    </p>
                  )}
                  <div className="absolute inset-x-0 bottom-0 bg-white/10 px-4 py-2 sm:px-5">
                    <div className="text-xs">
                      <a href="#" className="font-medium text-white/90 hover:text-white">
                        View all<span className="sr-only"> {item.name} stats</span>
                      </a>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </dl>

          <div className="flex flex-col md:flex-row gap-6 mt-8">
            <div className="flex-1 bg-white rounded-xl shadow-md p-4 flex flex-col items-center">
              <h2 className="text-base font-semibold text-gray-900 mb-2">Security Events Per Day</h2>
              <div className="flex gap-3 mb-3 items-center">
                <label className="text-xs text-gray-600">From</label>
                <input
                  type="date"
                  className="rounded-md border border-gray-300 text-xs px-2 py-1 focus:border-indigo-500 focus:ring-indigo-500"
                  value={chartStartDate}
                  onChange={e => setChartStartDate(e.target.value)}
                />
                <label className="text-xs text-gray-600">To</label>
                <input
                  type="date"
                  className="rounded-md border border-gray-300 text-xs px-2 py-1 focus:border-indigo-500 focus:ring-indigo-500"
                  value={chartEndDate}
                  onChange={e => setChartEndDate(e.target.value)}
                />
                {(chartStartDate || chartEndDate) && (
                  <button
                    className="text-xs text-indigo-500 hover:text-indigo-700 underline"
                    onClick={() => { setChartStartDate(''); setChartEndDate(''); }}
                  >
                    Clear
                  </button>
                )}
              </div>
              <div className="w-full flex justify-center">
                <VictoryChart
                  height={160}
                  width={320}
                  padding={{ top: 20, bottom: 40, left: 40, right: 10 }}
                  domainPadding={10}
                  theme={VictoryTheme.material}
                  containerComponent={
                    <VictoryVoronoiContainer
                      labels={({ datum }) => `${datum.x}\n${datum.y} events`}
                      labelComponent={
                        <VictoryTooltip
                          flyoutStyle={{ fill: "#fff", stroke: "#6366f1", strokeWidth: 0.5 }}
                          style={{ fontSize: 10, fill: "#222" }}
                        />
                      }
                    />
                  }
                >
                  <VictoryAxis
                    tickFormat={(t) => format(new Date(t), 'MM/dd')}
                    style={{
                      axis: { stroke: "#e5e7eb" },
                      tickLabels: { fontSize: 8, angle: -45, padding: 14, fill: "#6366f1" },
                      grid: { stroke: "rgba(99,102,241,0.08)", strokeDasharray: "4" },
                    }}
                  />
                  <VictoryAxis
                    dependentAxis
                    style={{
                      axis: { stroke: "#e5e7eb" },
                      tickLabels: { fontSize: 10, fill: "#6366f1" },
                      grid: { stroke: "rgba(99,102,241,0.08)", strokeDasharray: "4" },
                    }}
                  />
                  <VictoryLine
                    data={chartData}
                    interpolation="monotoneX"
                    style={{
                      data: {
                        stroke: "#6366f1",
                        strokeWidth: 2,
                        filter: "drop-shadow(0 1px 2px #6366f133)",
                      },
                    }}
                  />
                </VictoryChart>
              </div>
            </div>

            {selectedDate && (
              <div className="flex-1 bg-white rounded-xl shadow-md p-4 flex flex-col items-center">
                <h2 className="text-base font-semibold text-gray-900 mb-2">
                  Security Events by Hour ({selectedDate})
                </h2>
                <div className="w-full flex justify-center">
                  <VictoryChart
                    height={160}
                    width={320}
                    padding={{ top: 20, bottom: 40, left: 40, right: 10 }}
                    domainPadding={{ x: 6, y: 10 }}
                    theme={VictoryTheme.material}
                    containerComponent={
                      <VictoryVoronoiContainer
                        labels={({ datum }) => `${datum.x}\n${datum.y} events`}
                        labelComponent={
                          <VictoryTooltip
                            flyoutStyle={{ fill: "#fff", stroke: "#6366f1", strokeWidth: 0.5 }}
                            style={{ fontSize: 10, fill: "#222" }}
                          />
                        }
                      />
                    }
                  >
                    <VictoryAxis
                      tickValues={barChartData.map(d => d.x)}
                      tickFormat={(t, i) => i % 4 === 0 ? t : ''}
                      style={{
                        axis: { stroke: "#e5e7eb" },
                        tickLabels: { fontSize: 7, angle: -45, padding: 10, fill: "#6366f1" },
                        grid: { stroke: "rgba(99,102,241,0.08)", strokeDasharray: "4" },
                      }}
                    />
                    <VictoryAxis
                      dependentAxis
                      style={{
                        axis: { stroke: "#e5e7eb" },
                        tickLabels: { fontSize: 10, fill: "#6366f1" },
                        grid: { stroke: "rgba(99,102,241,0.08)", strokeDasharray: "4" },
                      }}
                    />
                    <VictoryBar
                      data={barChartData}
                      style={{
                        data: {
                          fill: "url(#barChartGradient)",
                          stroke: "#6366f1",
                          strokeWidth: 1,
                          filter: "drop-shadow(0 1px 2px #6366f133)",
                        },
                      }}
                      barWidth={8}
                    />
                  </VictoryChart>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="px-4 sm:px-6 lg:px-8 mt-10">
        <div className="mb-6 border-b border-gray-200">
          <nav className="-mb-px flex space-x-8" aria-label="Tabs">
            {TABS.map(tab => (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key)}
                className={classNames(
                  activeTab === tab.key
                    ? 'border-indigo-500 text-indigo-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300',
                  'whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm focus:outline-none'
                )}
              >
                {tab.label}
              </button>
            ))}
          </nav>
        </div>
        {/* Tab Content */}
        {activeTab === 'security' && renderSecurityTable()}
        {activeTab === 'logins' && renderLoginsTable()}
        {activeTab === 'bannedIps' && renderBannedIpsTable()}
        {activeTab === 'loginAttempts' && renderLoginAttemptsTable()}
        {activeTab === 'checklists' && renderChecklistsTable()}
      </div>
    </>
  );
}
